import { Assert } from 'shared/assert';
import { computed, ref, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { featureCollection, lineString, midpoint, point, polygon } from '@turf/turf';
import { geoJsonStyle } from 'shared/map/domain/style';
import { getGeoJsonPropertyNumber, setGeoJsonProperty } from 'shared/map/domain/geojson';
import { setShapeId } from 'shared/shapes/domain/shapeGeoJson';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Point, type Geometry } from 'ol/geom';
import type Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON.js';
import { getDragPan } from 'shared/map/domain/interactions';
import CircleStyle from 'ol/style/Circle.js';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { Color } from 'shared/common/domain/color';
import { usePreviewPolygonStore } from '../stores/previewPolygonStore';

const movePointRadius = 7;
const insertPointRadius = 5;

interface PreviewPolygonLayerOptions {
  selectionEnabled: Ref<boolean>;
  selectedShapeIds: Ref<string[]>;
}

export function usePreviewPolygonLayer(options: PreviewPolygonLayerOptions) {
  const active = ref<boolean>(true);
  const selectionEnabled = options.selectionEnabled;
  const selectedShapeIds = options.selectedShapeIds;

  const previewPolygonStore = usePreviewPolygonStore();
  const geojson = new GeoJSON();

  const moveOrigin = ref<GeoJSON.Position>();

  const polygonMoving = computed(() => {
    return operation.value === 'update' && moveOrigin.value !== undefined;
  });

  const { points, operation, allGeoJsonShapes, multiEditMode } = storeToRefs(previewPolygonStore);

  const mousePoint = ref<GeoJSON.Position>();
  const dragingPointIndex = ref<number>();

  const shape = computed(() => {
    const features: GeoJSON.Feature[] = [];

    if (operation.value === 'none') return featureCollection([]);

    // In multi-edit mode, render all shapes
    if (multiEditMode.value) {
      for (const shapeGeoJson of allGeoJsonShapes.value) {
        const feature = shapeGeoJson.shape;
        const shapeStyle = geoJsonStyle(feature);
        shapeStyle.color = '#fef3c7';
        shapeStyle.outlineColor = '#000000';
        features.push(feature);
      }
      return featureCollection(features);
    }

    // Single shape mode
    let feature: GeoJSON.Feature | null = null;

    const previewPoints = [...points.value];

    if (operation.value === 'create' && mousePoint.value) {
      previewPoints.push(mousePoint.value);
    }

    const pointCount = previewPoints.length;

    if (pointCount > 2) {
      const firstPoint = previewPoints[0];
      if (firstPoint) {
        const polygonPoints = [...previewPoints, firstPoint];
        feature = polygon([polygonPoints]);
      }
    } else if (pointCount > 1) {
      feature = lineString(previewPoints);
    }

    if (feature === null) return featureCollection([]);

    const shapeStyle = geoJsonStyle(feature);

    shapeStyle.color = '#fef3c7';
    shapeStyle.outlineColor = '#000000';

    if (selectedShapeIds.value.length > 0) {
      setShapeId(feature, selectedShapeIds.value[0]!);
    }

    return featureCollection([feature]);
  });

  const renderPoints = computed(() => {
    const pointCount = points.value.length;

    // Don't show edit points in multi-edit mode. Can only move all shapes together, so individual edit points don't make sense.
    if (multiEditMode.value) return featureCollection([]);

    if (pointCount < 3) return featureCollection([]);

    const shapeId = selectedShapeIds.value[0];

    switch (operation.value) {
      case 'create':
        const createResult: GeoJSON.Feature[] = [];

        for (let i = 0; i < pointCount; i++) {
          const p = point(points.value[i]!);

          setRadius(p, movePointRadius);

          if (shapeId !== undefined) {
            setShapeId(p, shapeId);
          }

          // First and last point can finish the polygon
          if (i === 0 || i === pointCount - 1) {
            setIsFinishPoint(p, true);

            const style = geoJsonStyle(p);
            style.color = '#fef3c7';
            style.outlineColor = '#000000';
          } else {
            setIsMovePoint(p, true);
          }

          createResult.push(p);
        }

        return featureCollection(createResult);

      case 'update':
        const result: GeoJSON.Feature[] = [];

        let prev = points.value[pointCount - 1];
        if (prev) {
          for (let i = -1; i < pointCount - 1; i++) {
            const next = points.value[i + 1];
            if (next) {
              const midPoint = midpoint(prev, next);
              setRadius(midPoint, insertPointRadius);
              setIndex(midPoint, i + 1);
              setIsCreatePoint(midPoint, true);
              if (shapeId !== undefined) {
                setShapeId(midPoint, shapeId);
              }
              result.push(midPoint);

              const movePoint = point(next);
              setRadius(movePoint, movePointRadius);
              setIndex(movePoint, i + 1);
              setIsMovePoint(movePoint, true);
              if (shapeId !== undefined) {
                setShapeId(movePoint, shapeId);
              }
              result.push(movePoint);

              prev = next;
            }
          }
        }

        return featureCollection(result);
    }

    return featureCollection([]);
  });

  const hoverMovePoint = ref<boolean>(false);
  const hoverInsertPoint = ref<boolean>(false);
  const hoverPolygon = ref<boolean>(false);

  const cursor = computed(() => {
    if (!active.value) return undefined;
    if (operation.value === 'create') return 'crosshair';
    if (polygonMoving.value || dragingPointIndex.value !== undefined) return 'grabbing';
    if (hoverMovePoint.value) return 'move';
    if (hoverInsertPoint.value) return 'crosshair';
    if (hoverPolygon.value) return 'move';
    return undefined;
  });

  const dragPanEnabled = computed(
    () => !polygonMoving.value && dragingPointIndex.value === undefined,
  );

  function attachListeners(map: OLMap) {
    const element = map.getTargetElement();

    map.on('click', (ev) => {
      if (!active.value) return;
      if (operation.value !== 'update') return;

      const features = map.getFeaturesAtPixel(ev.pixel);

      for (const feature of features) {
        const properties = feature.getProperties();
        const isCreatePoint = properties['isCreatePoint'] as undefined | boolean;
        if (!isCreatePoint) continue;
        const geometry = feature.getGeometry();
        if (!geometry || geometry.getType() !== 'Point') continue;
        const point = geometry as Point;
        const index = properties['index'] as number;
        if (index === undefined) continue;
        previewPolygonStore.insertPoint(index, point.getCoordinates());
        hoverInsertPoint.value = false;
        ev.preventDefault();
        return;
      }
    });

    element.addEventListener('contextmenu', (ev) => {
      if (!active.value) return;
      if (operation.value !== 'update') return;
      if (!ev.ctrlKey) return;

      const pixel = map.getEventPixel(ev);
      const features = map.getFeaturesAtPixel(pixel);

      for (const feature of features) {
        const properties = feature.getProperties();
        const isMovePoint = properties['isMovePoint'] as undefined | boolean;
        if (!isMovePoint) continue;
        const geometry = feature.getGeometry();
        if (!geometry || geometry.getType() !== 'Point') continue;
        const index = properties['index'] as number;
        if (index === undefined) continue;
        previewPolygonStore.deletePoint(index);
        ev.preventDefault();
        ev.stopImmediatePropagation();
        return;
      }
    });
  }

  function attach(map: OLMap) {
    const { source, pointSource, pointLayer } = createLayer(map);
    const dragPan = getDragPan(map);

    const element = map.getTargetElement();

    watch(cursor, (newCursor) => {
      if (newCursor) {
        element.style.cursor = newCursor;
      } else {
        element.style.removeProperty('cursor');
      }
    });

    watch(dragPanEnabled, (enabled) => {
      if (!dragPan) return;
      dragPan.setActive(enabled);
    });

    watch(operation, (newOperation) => {
      switch (newOperation) {
        case 'create':
          selectionEnabled.value = false;
          break;
        case 'update':
          selectionEnabled.value = true;
          break;
        case 'none':
          selectionEnabled.value = true;
          break;
      }
    });

    element.addEventListener('mousemove', (ev) => {
      hoverMovePoint.value = false;
      hoverInsertPoint.value = false;

      if (!active.value) return;

      const pixel = map.getEventPixel(ev);
      const features = map.getFeaturesAtPixel(pixel, {
        layerFilter: (layer) => layer === pointLayer,
      });

      if (features.length === 0) return;

      for (const feature of features) {
        const properties = feature.getProperties();
        const isMovePoint = properties['isMovePoint'];

        if (isMovePoint) {
          hoverMovePoint.value = true;
          return;
        }

        const isCreatePoint = properties['isCreatePoint'];

        if (isCreatePoint) {
          hoverInsertPoint.value = true;
          return;
        }
      }
    });

    element.addEventListener('mousedown', (ev) => {
      if (!active.value) return;
      if (ev.button !== 0) return;
      if (!map.getView()) return;

      const pixel = map.getEventPixel(ev);
      const points = map.getFeaturesAtPixel(pixel, {
        layerFilter: (layer) => layer === pointLayer,
      });

      for (const feature of points) {
        const properties = feature.getProperties();
        const isMovePoint = properties['isMovePoint'];
        if (isMovePoint) {
          const index = properties['index'] as number | undefined;
          if (index === undefined) continue;
          ev.preventDefault();
          dragingPointIndex.value = index;
          return;
        }
      }

      const coordinate = map.getEventCoordinate(ev);
      if (!coordinate) return;
      const features = source.getFeaturesAtCoordinate(coordinate);
      if (features.length === 0) return;
      ev.preventDefault();
      const moveCoordinate = map.getEventCoordinate(ev);
      if (!moveCoordinate) return;
      moveOrigin.value = moveCoordinate;
    });

    element.addEventListener('mouseup', () => {
      dragingPointIndex.value = undefined;
      moveOrigin.value = undefined;
    });

    element.addEventListener('mouseleave', () => {
      hoverPolygon.value = false;
      hoverMovePoint.value = false;
      hoverInsertPoint.value = false;
    });

    element.addEventListener('mousemove', (ev) => {
      hoverPolygon.value = false;
      if (!active.value) return;
      if (operation.value !== 'update') return;
      if (!map.getView()) return;
      const coordinate = map.getEventCoordinate(ev);
      if (!coordinate) return;
      const features = source.getFeaturesAtCoordinate(coordinate);
      hoverPolygon.value = features.length > 0;
    });

    element.addEventListener('click', (ev) => {
      if (!active.value) return;
      if (operation.value !== 'create') return;
      if (!map.getView()) return;

      const features = map.getFeaturesAtPixel(map.getEventPixel(ev), {
        layerFilter: (layer) => layer === pointLayer,
      });

      for (const feature of features) {
        const properties = feature.getProperties();
        const isFinishPoint = properties['isFinishPoint'];

        if (isFinishPoint) {
          previewPolygonStore.finish();
          return;
        }
      }

      const coordinate = map.getEventCoordinate(ev);
      if (!coordinate) return;
      previewPolygonStore.addPoint(coordinate);
    });

    element.addEventListener('mousemove', (ev) => {
      if (!active.value) return;
      if (!map.getView()) return;

      try {
        const coordinate = map.getEventCoordinate(ev);
        if (!coordinate) return;
        Assert.definied(coordinate[0]);
        Assert.definied(coordinate[1]);
        mousePoint.value = coordinate;

        if (operation.value === 'update') {
          if (dragingPointIndex.value !== undefined) {
            previewPolygonStore.updatePoint(dragingPointIndex.value, coordinate);
          } else {
            if (
              moveOrigin.value === undefined ||
              moveOrigin.value[0] === undefined ||
              moveOrigin.value[1] === undefined
            ) {
              return;
            }
            const deltaX = coordinate[0] - moveOrigin.value[0];
            const deltaY = coordinate[1] - moveOrigin.value[1];

            previewPolygonStore.move(deltaX, deltaY);

            moveOrigin.value = coordinate;
          }
        }
      } catch (error) {}
    });

    watch(shape, (newShape) => {
      source.clear();
      const features = geojson.readFeatures(newShape) as Feature<Geometry>[];

      // Set IDs on features so click handling works correctly
      for (const feature of features) {
        const shapeId = feature.get('shapeId') as string | undefined;
        if (shapeId) {
          feature.setId(shapeId);
        }
      }

      source.addFeatures(features);
    });

    watch(renderPoints, (newFinishPoints) => {
      pointSource.clear();
      const features = geojson.readFeatures(newFinishPoints);
      pointSource.addFeatures(features);
    });

    return source;
  }

  return {
    attach,
    attachListeners,
    setActive(value: boolean) {
      active.value = value;
    },
  };
}

const isFinishPointKey = 'isFinishPoint';

function setIsFinishPoint(feature: GeoJSON.Feature, value: boolean) {
  setGeoJsonProperty(feature, isFinishPointKey, value);
}

const isMovePointKey = 'isMovePoint';

function setIsMovePoint(feature: GeoJSON.Feature, value: boolean) {
  setGeoJsonProperty(feature, isMovePointKey, value);
}

const isCreatePointKey = 'isCreatePoint';

function setIsCreatePoint(feature: GeoJSON.Feature, value: boolean) {
  setGeoJsonProperty(feature, isCreatePointKey, value);
}

const radiusKey = 'radius';

function setRadius(feature: GeoJSON.Feature, value: number) {
  setGeoJsonProperty(feature, radiusKey, value);
}

export function getRadius(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, radiusKey);
}

const indexKey = 'index';

function setIndex(feature: GeoJSON.Feature, value: number) {
  setGeoJsonProperty(feature, indexKey, value);
}

function createLayer(map: OLMap) {
  const source = new VectorSource();
  const layer = new VectorLayer({
    source,
    style: (feature) => {
      const properties = feature.getProperties();
      const color = properties['color'];
      const outlineColor = properties['outlineColor'];

      return new Style({
        fill: new Fill({
          color: Color.setAlpha(color, 0.8),
        }),
        stroke: new Stroke({
          color: Color.setAlpha(outlineColor, 0.8),
          width: 1,
        }),
      });
    },
  });
  layer.set('name', 'previewPolygonLayer');
  map.addLayer(layer);

  const pointSource = new VectorSource();
  const pointLayer = new VectorLayer({
    source: pointSource,
    style: (feature) => {
      const properties = feature.getProperties();
      const radius = properties['radius'] ?? 5;

      const style = new Style({
        image: new CircleStyle({
          radius: radius,
          fill: new Fill({
            color: '#007cbf',
          }),
        }),
      });
      return style;
    },
  });
  map.addLayer(pointLayer);

  return {
    source,
    layer,
    pointSource,
    pointLayer,
  };
}
